import React from "react";
import { View, StyleSheet, ScrollView } from "react-native";

import ResponseCard from "../ResponseCard/ResponseCard";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";

interface Author {
  id: string;
  name: string;
}

interface Reply {
  id: string;
  message: string;
  depth: number;
  created: string;
  author: Author;
}

interface Post {
  id: string;
  message: string;
  depth: number;
  threadId: string;
  created: string;
  lastModified: string;
  author: Author;
  replies: Reply[];
}

interface ResponseCardListProps {
  posts: Post[];
  onReply: (postId: string, message: string) => void;
  onPostViewed: (postId: string) => void;
  currentUser: {
    id: string;
    name: string;
  };
  loading?: boolean;
  error?: string;
}

const ResponseCardList: React.FC<ResponseCardListProps> = ({
  posts,
  onReply,
  onPostViewed,
  currentUser,
  loading = false,
  error,
}) => {
  // Filter out the root post (depth 0) to only show responses (depth 1)
  const responsePosts = posts.filter((post) => post.depth === 1);

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <CustomText text="Loading responses..." size="m" style={styles.loadingText} useTranslationText={false} />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.centerContainer}>
        <CustomText
          text="Error loading responses"
          size="m"
          weight="medium"
          style={styles.errorTitle}
          useTranslationText={false}
        />
        <CustomSpacing size="s" type="vertical" />
        <CustomText text={error} size="s" style={styles.errorMessage} useTranslationText={false} />
      </View>
    );
  }

  if (responsePosts.length === 0) {
    return (
      <View style={styles.centerContainer}>
        <CustomText
          text="No responses yet"
          size="l"
          weight="medium"
          style={styles.emptyTitle}
          useTranslationText={false}
        />
        <CustomSpacing size="s" type="vertical" />
        <CustomText
          text="Be the first to share your thoughts on this discussion."
          size="m"
          style={styles.emptyDescription}
          useTranslationText={false}
        />
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.container}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={styles.contentContainer}
      tabIndex={0}>
      {responsePosts.map((post) => (
        <ResponseCard
          key={post.id}
          post={post}
          onReply={onReply}
          onPostViewed={onPostViewed}
          currentUser={currentUser}
        />
      ))}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingVertical: 16,
  },
  centerContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 60,
    paddingHorizontal: 32,
  },
  loadingText: {
    color: "#6B7280",
  },
  errorTitle: {
    color: "#101828",
    textAlign: "center",
  },
  errorMessage: {
    color: "#6B7280",
    textAlign: "center",
    maxWidth: 300,
  },
  emptyTitle: {
    color: "#101828",
    textAlign: "center",
  },
  emptyDescription: {
    color: "#6B7280",
    textAlign: "center",
    maxWidth: 400,
  },
});

export default ResponseCardList;
